import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { PALETTES } from '../contexts/ThemeContext';
import { useFlightLog } from '../contexts/FlightLogContext';
import AuthModal from './AuthModal';
import { Plane, Menu, Home, Compass, Map, Bell, User, LogOut, Crown, Moon, Sun, Shield, Radio, Globe, BookOpen, Info, MapPin, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Logo using the official SVG wordmark (icon + Fly'nOut text)
// Transparent background — works on light and dark themes
export const FlyNOutLogo = ({ className = '' }) => (
  <img
    src="/logo.svg"
    alt="Fly'N Out"
    className={`h-9 sm:h-10 w-auto object-contain ${className}`}
    style={{ display: 'block' }}
  />
);

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme, palette, setPalette } = useTheme();
  const { open: openFlightLog } = useFlightLog();
  const navigate = useNavigate();
  const location = useLocation();
  const [showAuth, setShowAuth] = useState(false);
  const [authTab, setAuthTab] = useState('login');
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [recentlyVisited, setRecentlyVisited] = useState([]);
  const paletteRef = useRef(null);

  // Close palette picker when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (paletteRef.current && !paletteRef.current.contains(e.target)) {
        setPaletteOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const openLogin = () => { setAuthTab('login'); setShowAuth(true); };
  const openRegister = () => { setAuthTab('register'); setShowAuth(true); };

  const navLinks = [
    { href: '/discover',  label: 'Discover',  icon: Compass },
    { href: '/routes',    label: 'Routes',    icon: Plane },
    { href: '/tracker',   label: 'Tracker',   icon: Radio, live: true },
    { href: '/airlines',  label: 'Airlines',  icon: Globe },
    { href: '/community', label: 'Community', icon: Map },
    { href: '/blog',      label: 'Blog',      icon: BookOpen },
    { href: '/about',     label: 'About',     icon: Info },
  ];

  return (
    <>
      <nav className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="navbar-logo">
            <FlyNOutLogo />
          </Link>

          {/* Desktop Nav — visible lg+ only (7 items need 1024px+) */}
          <div className="hidden lg:flex items-center gap-0.5">
            {navLinks.map(link => {
              const isActive = location.pathname === link.href ||
                (link.href !== '/' && location.pathname.startsWith(link.href));
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`relative px-2.5 py-2 text-sm font-medium rounded-lg transition-colors hover:bg-muted ${
                    isActive ? 'text-[#FF4500]' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                  {link.live && (
                    <span className="ml-1 inline-flex items-center gap-0.5 text-[9px] font-bold text-[#34d399] bg-[#34d399]/15 px-1.5 py-0.5 rounded-full leading-none">
                      <span className="w-1 h-1 rounded-full bg-[#34d399] animate-pulse" />LIVE
                    </span>
                  )}
                  {isActive && (
                    <motion.div
                      layoutId="nav-active-indicator"
                      className="absolute bottom-0.5 left-3 right-3 h-0.5 bg-[#FF4500] rounded-full"
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-1">
            {/* Quick Log Flight — desktop only, FAB handles mobile */}
            <button
              onClick={openFlightLog}
              className="hidden lg:inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-[#FF4500] hover:bg-[#E63F00] text-white text-xs font-bold transition-colors shadow-sm mr-1"
              data-testid="quick-log-flight-btn"
              title="Log a flight"
            >
              <Plane className="w-3 h-3" /> Log
            </button>

            {/* Dark/light toggle */}
            <button
              onClick={toggleTheme}
              className="p-1.5 sm:p-2 rounded-lg hover:bg-muted transition-colors"
              aria-label="Toggle theme"
              data-testid="theme-toggle-switch"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            {/* Palette picker — hidden on small mobile */}
            <div ref={paletteRef} className="relative hidden sm:block">
              <button
                onClick={() => setPaletteOpen(v => !v)}
                className="p-2 rounded-lg hover:bg-muted transition-colors flex items-center"
                aria-label="Change colour palette"
                data-testid="palette-picker-btn"
              >
                <div
                  className="w-4 h-4 rounded-full border-2 border-white/60 shadow-sm"
                  style={{ background: PALETTES[palette]?.swatch || '#7EC8E3' }}
                />
              </button>
              {paletteOpen && (
                <div className="absolute right-0 top-11 animate-fade-in-up bg-popover border border-border rounded-2xl shadow-xl p-3 z-50 min-w-[180px]"
                  data-testid="palette-picker-panel">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-2.5 px-1">Colour Palette</p>
                  <div className="space-y-1">
                    {Object.entries(PALETTES).map(([key, p]) => (
                      <button
                        key={key}
                        onClick={() => { setPalette(key); setPaletteOpen(false); }}
                        className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-xl text-sm transition-all hover:bg-muted/70 ${palette === key ? 'bg-muted font-semibold' : ''}`}
                        data-testid={`palette-option-${key}`}
                      >
                        <div
                          className={`w-5 h-5 rounded-full flex-shrink-0 border-2 transition-all ${palette === key ? 'border-[#FF4500] scale-110' : 'border-transparent'}`}
                          style={{ background: p.swatch }}
                        />
                        <div className="text-left min-w-0">
                          <span className="block text-xs font-medium">{p.name}</span>
                          <span className="block text-[10px] text-muted-foreground truncate">{p.description}</span>
                        </div>
                        {palette === key && (
                          <span className="ml-auto text-[#FF4500] text-xs">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {user ? (
              <>
                {/* Notifications */}
                <button className="p-2 rounded-lg hover:bg-muted transition-colors relative" data-testid="notifications-btn">
                  <Bell className="w-4 h-4" />
                </button>

                {/* User menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="user-menu-btn">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={user.avatar} alt={user.name} />
                        <AvatarFallback className="bg-[#FF4500] text-white text-xs">
                          {user.name?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      {user.is_premium && (
                        <Badge className="hidden sm:inline-flex bg-[#FF4500]/15 text-[#FF4500] border border-[#FF4500]/30 text-[10px] px-1.5 py-0">
                          <Crown className="w-2.5 h-2.5 mr-0.5" /> Club
                        </Badge>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-semibold">{user.name}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to={`/profile/${user.user_id}`} className="flex items-center gap-2 cursor-pointer" data-testid="profile-link">
                        <User className="w-4 h-4" /> My Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer" data-testid="dashboard-link">
                        <Home className="w-4 h-4" /> Dashboard
                      </Link>
                    </DropdownMenuItem>
                    {!user.is_premium && (
                      <DropdownMenuItem asChild>
                        <Link to="/premium" className="flex items-center gap-2 cursor-pointer text-[#FF4500]" data-testid="upgrade-link">
                          <Crown className="w-4 h-4" /> Upgrade to Club
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {user.role === 'admin' && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2 cursor-pointer" data-testid="admin-link">
                          <Shield className="w-4 h-4" /> Admin
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 text-red-500 cursor-pointer" data-testid="logout-btn">
                      <LogOut className="w-4 h-4" /> Log Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={openLogin} data-testid="login-btn" className="hidden sm:inline-flex">
                  Log In
                </Button>
                <Button size="sm" onClick={openRegister} data-testid="signup-btn" className="fly-btn-primary">
                  Join Free
                </Button>
              </div>
            )}

            {/* Mobile menu — visible below lg */}
            <Sheet onOpenChange={(open) => {
              if (open) {
                try {
                  setRecentlyVisited(JSON.parse(localStorage.getItem('fly_recently_visited_v1') || '[]'));
                } catch {}
              }
            }}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 flex flex-col">
                <div className="flex flex-col gap-1 mt-4 flex-1 overflow-y-auto">

                  {/* Quick log button */}
                  <button
                    onClick={() => { openFlightLog(); }}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl fly-btn-glossy text-white font-semibold text-sm mb-2"
                    data-testid="mobile-menu-log-flight-btn"
                  >
                    <Plane className="w-4 h-4 flex-shrink-0" />
                    <span>Log a Flight</span>
                  </button>

                  {/* All nav links */}
                  {[{ href: '/', label: 'Home', icon: Home }, ...navLinks].map(link => {
                    const isActive = location.pathname === link.href ||
                      (link.href !== '/' && location.pathname.startsWith(link.href));
                    const Icon = link.icon;
                    return (
                      <Link
                        key={link.href}
                        to={link.href}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors font-medium text-sm ${
                          isActive ? 'bg-[#FF4500]/10 text-[#FF4500]' : 'hover:bg-muted text-foreground'
                        }`}
                        data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                      >
                        {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
                        <span>{link.label}</span>
                        {link.live && (
                          <span className="ml-auto inline-flex items-center gap-0.5 text-[9px] font-bold text-[#34d399] bg-[#34d399]/15 px-1.5 py-0.5 rounded-full">
                            <span className="w-1 h-1 rounded-full bg-[#34d399] animate-pulse" />LIVE
                          </span>
                        )}
                      </Link>
                    );
                  })}

                  {user && (
                    <Link to="/dashboard" className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-muted transition-colors text-sm font-medium" data-testid="mobile-nav-dashboard">
                      <Map className="w-4 h-4 flex-shrink-0" /> Dashboard
                    </Link>
                  )}

                  {/* Recently visited */}
                  {recentlyVisited.length > 0 && (
                    <div className="mt-3">
                      <div className="border-t border-border mb-3" />
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest px-3 mb-2 flex items-center gap-1.5">
                        <Clock className="w-3 h-3" /> Recently Visited
                      </p>
                      <div className="space-y-0.5">
                        {recentlyVisited.map((item, i) => (
                          <Link
                            key={i}
                            to={item.href}
                            className="flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-muted transition-colors group"
                            data-testid="recently-visited-item"
                          >
                            <div className="w-7 h-7 rounded-lg bg-[#FF4500]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#FF4500]/20 transition-colors">
                              {item.type === 'airport'
                                ? <MapPin className="w-3.5 h-3.5 text-[#FF4500]" />
                                : item.type === 'route'
                                ? <Plane className="w-3.5 h-3.5 text-[#FF4500]" />
                                : <BookOpen className="w-3.5 h-3.5 text-[#FF4500]" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate">{item.label}</p>
                              {item.sublabel && <p className="text-[10px] text-muted-foreground truncate">{item.sublabel}</p>}
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="border-t border-border mt-3 mb-2" />

                  {!user ? (
                    <>
                      <Button variant="outline" onClick={openLogin} className="w-full">Log In</Button>
                      <Button onClick={openRegister} className="fly-btn-primary w-full">Join Free</Button>
                    </>
                  ) : (
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-muted transition-colors text-sm font-medium text-red-500 w-full text-left"
                    >
                      <LogOut className="w-4 h-4" /> Log Out
                    </button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      <AuthModal open={showAuth} onOpenChange={setShowAuth} defaultTab={authTab} />
    </>
  );
}
